import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Loader2,
  Music,
  AlertCircle,
  Rewind,
  Radio,
  Info,
  Clock,
  CircleDot
} from 'lucide-react';
import { devices as devicesApi } from '../../services/api';
import api from '../../services/api';
import './recording-dialog.css';

interface NewRecordingDialogProps {
  open: boolean;
  onClose: () => void;
  onRecordingStarted?: () => void;
}

type RecordingMode = 'live' | 'past' | 'scheduled';

const NewRecordingDialog: React.FC<NewRecordingDialogProps> = ({
  open,
  onClose,
  onRecordingStarted
}) => {
  const [devices, setDevices] = useState<any[]>([]);
  const [selectedDevice, setSelectedDevice] = useState('');
  const [title, setTitle] = useState('');
  const [mode, setMode] = useState<RecordingMode>('live');
  const [pastMinutes, setPastMinutes] = useState(30);
  const [futureMinutes, setFutureMinutes] = useState(30);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      loadDevices();
      // Reset to default state - LIVE mode
      setMode('live');
      setPastMinutes(30);
      setFutureMinutes(30);
      setTitle('');
      setError('');
    }
  }, [open]);

  const loadDevices = async () => {
    try {
      const response = await devicesApi.list();
      setDevices(response.data);
      if (response.data.length > 0) {
        setSelectedDevice(response.data[0].id);
      }
    } catch (err) {
      console.error('Failed to load devices:', err);
      setError('Failed to load devices');
    }
  };

  const handleTriggerRecording = async () => {
    if (!selectedDevice || !title.trim()) {
      setError('Please select a device and enter a title');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const payload: any = {
        deviceId: selectedDevice,
        title: title.trim(),
        timestamp: new Date().toISOString()
      };

      // Set recording mode based on selected mode
      if (mode === 'live') {
        // Live recording - open-ended until manually stopped
        payload.recordingMode = 'open';
        payload.captureMinutes = 0;
        payload.preRollMinutes = 0;
      } else if (mode === 'past') {
        // Capture past audio from buffer
        payload.recordingMode = 'past';
        payload.preRollMinutes = pastMinutes;
        payload.captureMinutes = 0;
      } else if (mode === 'scheduled') {
        // Schedule future recording with duration
        payload.recordingMode = 'future';
        payload.captureMinutes = futureMinutes;
        payload.preRollMinutes = 0;
      }

      const response = await api.post('/devices/trigger-recording', payload);

      if (response.data.success) {
        onRecordingStarted?.();
        onClose();
      } else {
        setError(response.data.message || 'Failed to trigger recording');
      }
    } catch (err: any) {
      console.error('Failed to trigger recording:', err);
      setError(err.response?.data?.error || 'Failed to trigger recording');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDuration = (minutes: number) => {
    if (minutes === 0) return '0 min';
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${mins}m`;
  };

  const getModeDescription = () => {
    if (mode === 'live') {
      return "Start recording now and manually stop when finished";
    } else if (mode === 'past') {
      return `Capture the last ${formatDuration(pastMinutes)} from the device's audio buffer`;
    } else {
      return `Record for ${formatDuration(futureMinutes)} starting now`;
    }
  };

  const pastPresets = [15, 30, 60, 120];
  const futurePresets = [15, 30, 60, 120, 180];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center text-xl">
            <Radio className="mr-2 h-5 w-5 text-red-600 dark:text-red-400" />
            New Recording
          </DialogTitle>
          <DialogDescription>
            Choose how you want to capture audio
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {error && (
            <Alert className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
              <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
              <AlertDescription className="text-red-800 dark:text-red-300">{error}</AlertDescription>
            </Alert>
          )}

          {/* Device and Title */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="device">Device</Label>
              <select
                id="device"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                value={selectedDevice}
                onChange={(e) => setSelectedDevice(e.target.value)}
              >
                <option value="">Choose a device</option>
                {devices.map((device) => (
                  <option key={device.id} value={device.id}>
                    {device.name} - {device.venue_name || 'No venue'}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Recording name</Label>
              <Input
                id="title"
                placeholder="e.g., DJ Set, Live Performance"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="text-base"
              />
            </div>
          </div>

          {/* Mode Selection - Primary: Live, Secondary: Past/Scheduled */}
          <div className="space-y-4">
            <Label className="text-base font-medium">Recording Mode</Label>

            {/* Mode Tabs */}
            <div className="grid grid-cols-3 gap-2 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
              <button
                type="button"
                onClick={() => setMode('live')}
                className={`flex flex-col items-center gap-1 px-4 py-3 rounded-md transition-all ${
                  mode === 'live'
                    ? 'bg-white dark:bg-gray-700 shadow-sm text-red-600 dark:text-red-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <CircleDot className={`h-5 w-5 ${mode === 'live' ? 'animate-pulse' : ''}`} />
                <span className="text-sm font-medium">Live</span>
              </button>

              <button
                type="button"
                onClick={() => setMode('past')}
                className={`flex flex-col items-center gap-1 px-4 py-3 rounded-md transition-all ${
                  mode === 'past'
                    ? 'bg-white dark:bg-gray-700 shadow-sm text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <Rewind className="h-5 w-5" />
                <span className="text-sm font-medium">Past</span>
              </button>

              <button
                type="button"
                onClick={() => setMode('scheduled')}
                className={`flex flex-col items-center gap-1 px-4 py-3 rounded-md transition-all ${
                  mode === 'scheduled'
                    ? 'bg-white dark:bg-gray-700 shadow-sm text-green-600 dark:text-green-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <Clock className="h-5 w-5" />
                <span className="text-sm font-medium">Scheduled</span>
              </button>
            </div>

            {/* Mode-specific UI */}
            {mode === 'live' && (
              <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CircleDot className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 animate-pulse" />
                  <div className="flex-1">
                    <h4 className="font-medium text-red-900 dark:text-red-100 mb-1">
                      Live Recording
                    </h4>
                    <p className="text-sm text-red-800 dark:text-red-300">
                      The recording will start immediately and continue until you manually stop it.
                      Perfect for live performances, DJ sets, or unpredictable duration events.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {mode === 'past' && (
              <div className="space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Rewind className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-1">
                        Capture Past Audio
                      </h4>
                      <p className="text-sm text-blue-800 dark:text-blue-300">
                        Retrieve audio that was already recorded in the device's rolling buffer.
                        Great for capturing something that just happened.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="past-duration">Duration to capture</Label>
                  <div className="flex gap-2">
                    {pastPresets.map((preset) => (
                      <Button
                        key={preset}
                        type="button"
                        variant={pastMinutes === preset ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPastMinutes(preset)}
                        className="flex-1"
                      >
                        {formatDuration(preset)}
                      </Button>
                    ))}
                  </div>
                  <Input
                    id="past-duration"
                    type="number"
                    min="1"
                    max="240"
                    value={pastMinutes}
                    onChange={(e) => setPastMinutes(parseInt(e.target.value) || 30)}
                    className="text-center"
                  />
                </div>
              </div>
            )}

            {mode === 'scheduled' && (
              <div className="space-y-4">
                <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-medium text-green-900 dark:text-green-100 mb-1">
                        Scheduled Recording
                      </h4>
                      <p className="text-sm text-green-800 dark:text-green-300">
                        Record for a specific duration starting now. The recording will
                        automatically stop after the time is up.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="future-duration">Recording duration</Label>
                  <div className="flex gap-2">
                    {futurePresets.map((preset) => (
                      <Button
                        key={preset}
                        type="button"
                        variant={futureMinutes === preset ? "default" : "outline"}
                        size="sm"
                        onClick={() => setFutureMinutes(preset)}
                        className="flex-1"
                      >
                        {formatDuration(preset)}
                      </Button>
                    ))}
                  </div>
                  <Input
                    id="future-duration"
                    type="number"
                    min="1"
                    max="360"
                    value={futureMinutes}
                    onChange={(e) => setFutureMinutes(parseInt(e.target.value) || 30)}
                    className="text-center"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Summary */}
          <Alert className="border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20">
            <Info className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            <AlertDescription className="text-purple-900 dark:text-purple-300 font-medium">
              {getModeDescription()}
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleTriggerRecording}
            disabled={isLoading || !selectedDevice || !title.trim()}
            className={`${
              mode === 'live'
                ? 'bg-red-600 hover:bg-red-700'
                : mode === 'past'
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                {mode === 'live' && <CircleDot className="mr-2 h-4 w-4" />}
                {mode === 'past' && <Rewind className="mr-2 h-4 w-4" />}
                {mode === 'scheduled' && <Clock className="mr-2 h-4 w-4" />}
                {mode === 'live' ? 'Start Recording' : 'Start Capture'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewRecordingDialog;
